import React from 'react';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import { isMobile } from 'react-device-detect';
import moment from 'moment';
import { colors } from 'global/colors';
import { TimeAwayGraphContainer } from './styles';

export default React.memo(({ ticket, inputs, showFullOverTime }) => {
  const MAX_INTERVAL = 20;

  let lastInput = inputs.find((input) => input.isFinalSubmit && !input.isBestAfterDeadline);
  if (showFullOverTime)
    lastInput = inputs.sort((a, b) => {
      return (
        new Date(a.timeOfRequest ?? a.timeOfPaste ?? a.updatedAt) -
        new Date(b.timeOfRequest ?? b.timeOfPaste ?? b.updatedAt)
      );
    })[inputs.length - 1];

  let lastTime = new Date(lastInput.timeOfRequest || lastInput.timeOfPaste || lastInput.updatedAt);

  // makes it easier to debug
  let hideAxisAndTicks = true;

  const hereColors = {
    backgroundColor: hideAxisAndTicks ? 'white' : 'red',
    borderColor: hideAxisAndTicks ? 'white' : 'red',
    hoverBackgroundColor: 'white',
    pointHoverBorderColor: 'white',
  };

  const awayColors = {
    backgroundColor: colors.lightBlue6,
    borderColor: colors.lightBlue6,
    barThickness: 15,
  };

  let timeAway = [];

  // Remove any timeAway object that is outside of lastTime(overtime)
  ticket.timeAway.forEach((object) => {
    if (object.left && new Date(object.left) < new Date(lastTime)) {
      timeAway.push(object);
    }
  });
  ticket = { ...ticket, timeAway };

  if (timeAway.length === 0) return <></>;

  let stackedData = [];
  // Fill first bar from the time the ticket started until the time of the first left.
  stackedData.push({
    label: `here${-1}`,
    data: [new Date(ticket.timeAway[0]?.left).getTime() - new Date(ticket.startedAt).getTime()],
    ...hereColors,
  });

  for (let i = 0; i < ticket.timeAway.length; i++) {
    // If there are no more returns. We cant more. Handled outside loop to add a last bar
    if (!ticket.timeAway[i]?.returned) {
      continue;
    }

    let away = {
      label: `away${i}`,
      data: [new Date(ticket.timeAway[i]?.returned).getTime() - new Date(ticket.timeAway[i]?.left).getTime()],
      ...awayColors,
    };
    stackedData.push(away);

    // If there are no more lefts. We cant more. Handled outside loop to add a last bar
    if (!ticket.timeAway[i + 1]?.left) {
      continue;
    }
    let here = {
      label: `here${i}`,
      data: [new Date(ticket.timeAway[i + 1]?.left).getTime() - new Date(ticket.timeAway[i].returned).getTime()],
      ...hereColors,
    };
    stackedData.push(here);
  }

  // We need to add an extra bar at the end. To fill the space between the last left&return until the ticket is submitted.
  // There are two scenarios. The candidate left and never returned, so we fill with an away bar.
  // Or the candidate came back and we fill with an here bar.

  // The candidate left and never came back, fill with away from the time they left until ticket completed.
  if (!ticket.timeAway[ticket.timeAway.length - 1].returned) {
    stackedData.push({
      label: `away${ticket.timeAway.length - 1}`,
      data: [new Date(lastTime).getTime() - new Date(ticket.timeAway[ticket.timeAway.length - 1]?.left).getTime()],
      ...awayColors,
    });
  } else {
    // The candidate came back. Fill with here bar from the time of the last return until ticket completed.
    stackedData.push({
      label: `here${ticket.timeAway.length - 1}`,
      data: [new Date(lastTime).getTime() - new Date(ticket.timeAway[ticket.timeAway.length - 1]?.returned).getTime()],
      ...hereColors,
    });
  }

  const options = {
    type: 'bar',
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 0,
    },
    layout: {
      padding: {
        right: 30,
      },
    },
    indexAxis: 'y',
    scales: {
      x: {
        stacked: true,
        max: new Date(lastTime).getTime() - new Date(ticket.startedAt).getTime(),
        border: { color: hideAxisAndTicks ? 'white' : '' },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: MAX_INTERVAL,
          // Needs to have the same rotation as the score graph, otherwise it will not align
          maxRotation: 27,
          minRotation: 27,
          color: hideAxisAndTicks ? 'white' : '',
          font: { size: isMobile ? 8 : 12 },
          callback: (value, a, b) => {
            // if value/tick is after lastTime, dont show it.
            if (value > new Date(lastTime).getTime() - new Date(ticket.startedAt).getTime()) return '';
            let time = moment(value);
            let minutes = Math.floor(time / 60000);
            let seconds = ((time % 60000) / 1000).toFixed(0);

            return seconds === 60 ? minutes + 1 + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
          },
        },
      },
      y: {
        stacked: true,
        border: { color: hideAxisAndTicks ? 'white' : '' },
        grid: {
          display: false,
        },
        ticks: {
          display: true,
          color: hideAxisAndTicks ? 'white' : '',
          font: { size: isMobile ? 8 : 12 },
        },
        title: {
          display: true,
          text: 'Time away',
          position: 'top',
          font: { weight: 'bold', size: 13 },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'point',
        intersection: false,
        position: 'nearest',
        caretPadding: 10,
        filter: (context) => {
          // hide the tooltip for the "here" segments
          if (context.datasetIndex % 2 === 0) return false;
          return true;
        },
        callbacks: {
          title: (tooltipItems) => {
            const dataIndex = tooltipItems?.[0]?.datasetIndex;
            // dataIndex is undefined for all even values. Because of the tooltip filter.
            if (dataIndex === undefined) return '';

            // The datasetIndex includes the here segments, so we need to divide by 2 and round down to the right index for timeAway
            let { left, returned } = ticket.timeAway[Math.floor(dataIndex / 2)];
            // If the candidate left and never came back, use lastTime as the return time.
            if (!returned) returned = lastTime;

            let time = new Date(returned) - new Date(left);
            let minutes = Math.floor(time / 60000);
            let seconds = ((time % 60000) / 1000).toFixed(0);

            return `Away from the window: ${seconds === 60 ? minutes + 1 + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds}`;
          },
          label: () => ``,
        },
      },
      datalabels: {
        display: false,
      },
    },
  };

  const data = {
    // labels is 100 to match the score 100, so the graphs are positions correctly in relation to the score graph.
    labels: ['100'],
    datasets: stackedData,
  };

  return (
    <TimeAwayGraphContainer>
      <Bar id="leaveEventTimelineGraph" options={options} data={data} />
    </TimeAwayGraphContainer>
  );
});
